import React, { useState, useEffect } from "react";
import MainDashboardLayout from "../../../Layout/MainDashboardLayout";
import GRISidebar from "../../../Layout/GRISidebar";
import styles from "../../../styles/gri/general.module.css";
import {
  Box,
  Button,
  Alert,
  Snackbar,
  RadioGroup,
  FormControl,
  Radio,
  FormControlLabel,
} from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import { put, get } from "../../../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import SectionHeading from "../GRI300/GRI308/SectionHeading";
import CustomTextField from "../GRI300/GRI308/CustomTextField";
import ReviewBox from "../../../Components/Common/ReviewBox";
import { currentYear } from "../../../utils/generateYearRanges";

const GRI410 = ({hide}) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [disabled, setDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackOpen, setSnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [formData, setFormData] = useState({
    thirdPartyOrg: { status: true, remarks: "" },
    percentage: "",
  });
  const [griReview, setGriReview] = useState(() => JSON.parse(localStorage.getItem("griReview")));

  useEffect(() => {
    const handleStorage = () => {
      setGriReview(JSON.parse(localStorage.getItem("griReview")));
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    setGriReview(JSON.parse(localStorage.getItem("griReview")));
  }, []);

  const handleRadioChange = (event, section) => {
    const statusValue = event.target.value === "yes"; // Convert to boolean
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        status: statusValue,
      },
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`/gri/get?type=GRI400&Id=${id}`);
        if (response?.data?.data?.year < currentYear - 1) setDisabled(true);
        const data = response.data.data.GRI400?.securityPractices || {};
        setFormData({
          thirdPartyOrg: {
            remarks: data.thirdPartyOrg?.remarks || "",
            status: Boolean(data.thirdPartyOrg?.status || ""),
          },
          percentage: data.percentage || "",
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleNestedInputChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));

    // Remove error for the specific field
    if (value.trim()) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    let newErrors = {};
    let formIsValid = true;

    // Validate required fields in the formData
    if (formData.thirdPartyOrg.status) {
      // Validate `days` field
      if (!formData.thirdPartyOrg.remarks) {
        newErrors.remarks = "This field is required";
        formIsValid = false;
      }
    }

    // if (!formData.percentage) {
    //     newErrors.percentage = "This field is required";
    //     formIsValid = false;
    // } else
    if (isNaN(formData.percentage)) {
      newErrors.percentage = "Please enter a valid numeric value";
      formIsValid = false;
    } else if (formData.percentage < 0 || formData.percentage > 100) {
      newErrors.percentage = "Percentage must be between 0 and 100";
      formIsValid = false;
    }

    setErrors(newErrors);
    return formIsValid;
  };

  const handleSubmit = async () => {
    if (!disabled) {
      if (!validateForm()) return; // Validate form before submission

      const payload = {
        type: "GRI400",
        submitted410: true,
        securityPractices: {
          thirdPartyOrg: formData.thirdPartyOrg,
          percentage: formData.percentage,
        },
      };

      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
        await put(`/gri/update?Id=${id}`, payload);
        navigate(`/gri/gri400/411-1/${id}`);
      } catch (error) {
        setSnackOpen({
          open: true,
          message: "Error updating data. Please try again.",
          severity: "error",
        });
      }
    } else {
      navigate(`/gri/gri400/411-1/${id}`);
    }
  };


  const renderContent = ({hide}) => (
     <>
      {/* <SectionHeading text="2-1 Activities, value chain and other business relationships" /> */}
      <p className={styles.section_heading} style={{ marginBottom: hide ? "-1rem" : "0" }}>410 : Security Practices 2016</p>
      <p className={styles.main_heading} style={{ marginBottom: hide ? "-1.5rem" : "0" }}>
        410-1 : Security personnel trained in human rights policies or
        procedures
      </p>

      <SectionHeading
        text="a. What percentage of security personnel have undergone formal training on the organization’s human rights policies or specific procedures, including their application to security?"
        sx={{ marginTop: "2rem" }}
      />
      <Box sx={{ width: "40%", marginBottom: hide ? "-1.5rem" : "0", marginTop: hide ? "-1.5rem" : "0" }}>
        <CustomTextField
          value={formData.percentage}
          onChange={(e) => handleInputChange("percentage", e.target.value)}
          placeholder={"%"}
          error={errors.percentage}
          helperText={errors.percentage}
          disabled={disabled}
        />
      </Box>

      <SectionHeading
        text="b. Do the training requirements extend to third-party organizations that provide security personnel?"
        sx={{ marginTop: "2rem", marginBottom: hide ? "-1.5rem" : "-1rem" }}
      />
      <Box>
        <FormControl sx={{ margin: "1rem" }}>
          <RadioGroup
            row
            value={formData.thirdPartyOrg.status ? "yes" : "no"}
            onChange={(e) => handleRadioChange(e, "thirdPartyOrg")}
            disabled={disabled}
          >
            <FormControlLabel
              value="yes"
              control={<Radio disabled={disabled} />}
              label="Yes"
            />
            <FormControlLabel
              value="no"
              control={<Radio disabled={disabled} />}
              label="No"
            />
          </RadioGroup>
        </FormControl>
        {formData.thirdPartyOrg.status && (
          <CustomTextField
            value={formData.thirdPartyOrg.remarks}
            onChange={(e) =>
              handleNestedInputChange(
                "thirdPartyOrg",
                "remarks",
                e.target.value
              )
            }
            error={errors.remarks}
            helperText={errors.remarks}
            disabled={disabled}
          />
        )}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {/* Submit Button */}
       

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackOpen.open}
          message={snackOpen.message}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={() => setSnackOpen({ ...snackOpen, open: false })}
        >
          <Alert
            severity={snackOpen.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackOpen.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  )

  return (
    <>
      {!hide ? (
        <MainDashboardLayout sidebar={<GRISidebar />}>
          {renderContent({ hide })}

          <>
            {!griReview ? (
          <Box sx={{ ml: "1rem", width: "98%" }}>
            <ReviewBox url={"/gri"} handleSubmit={handleSubmit} />
          </Box>
        ) : (
          <Box textAlign="right" sx={{ mt: 3 }}>
            <Button
              variant="contained"
              sx={{ bgcolor: "#1C486B", textTransform: "none", mt: 3, mb: 5 }}
              onClick={handleSubmit}
            >
              Save & Continue <EastIcon sx={{ ml: 2 }} />
            </Button>
          </Box>
        )}
          </>
        </MainDashboardLayout>
      ) : (
        renderContent({ hide })
      )}
    </>
  );
};

export default GRI410;
